<script>
import Layout from "@/views/layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import { required } from "vuelidate/lib/validators";
import store from "@/state/store";

export default {
  beforeRouteEnter(to, from, next) {
    const userType = store.getters["auth/getUser"].user_type;
    const permissions = store.getters["auth/permissions"];
    if (
      (userType === 1 || userType === 3) &&
      permissions.includes("PAYROLL_CONFIG")
    ) {
      next();
    } else {
      alert("You are not allowed to access this page. You will be redirected.");
      next("/");
    }
  },
  page: {
    title: "Relief Types",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
  },
  mounted() {
    this.refreshTable();
  },
  validations: {
    reliefName: { required },
  },
  methods: {
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },

    async refreshTable() {
      await this.apiGet(this.ROUTES.reliefType, "Get Relief Types Error").then(
        (res) => {
          const { data } = res;
          this.reliefTypes = data.map((rt, index) => ({
            sn: index + 1,
            ...rt,
          }));
          this.totalRows = this.reliefTypes.length;
        }
      );
    },

    selectReliefType(rt) {
      rt = rt[0];
      this.reliefName = rt.relief_name;
      this.reliefID = rt.id;
      this.$refs["update-relief"].show();
      this.$refs["relief-table"].clearSelected();
    },

    resetForm() {
      this.reliefName = "";
      this.$v.$reset();
    },

    submitAdd() {
      this.submitted = true;
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.apiFormHandler("Invalid Relief Type");
      } else {
        const data = { relief_name: this.reliefName };
        const url = `${this.ROUTES.reliefType}/add-relief-type`;
        this.apiPost(url, data, "Add Relief Type Error").then((res) => {
          this.apiResponseHandler(
            `${res.data.relief_name} has been added successfully`,
            "New Relief Type Added"
          );
          this.refreshTable();
          this.$v.$reset();
          this.$refs["add-relief"].hide();
        });
      }
    },

    submitUpdate() {
      this.submitted = true;
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.apiFormHandler("Invalid Relief Type");
      } else {
        const data = { relief_name: this.reliefName };
        const url = `${this.ROUTES.reliefType}/${this.reliefID}`;
        this.apiPatch(url, data, "Update Relief Type Error").then((res) => {
          console.log(res);
          this.apiResponseHandler(`Action successful!`, "Update Successful");
          this.refreshTable();
          this.$v.$reset();
          this.$refs["update-relief"].hide();
        });
      }
    },
  },
  data() {
    return {
      title: "Relief Types",
      items: [
        { text: "IHUMANE" },
        { text: "Dashboard", href: "/" },
        { text: "Relief Types", active: true },
      ],
      reliefTypes: [],
      totalRows: 1,
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 25, 50, 100],
      filter: null,
      filterOn: [],
      sortBy: "sn",
      sortDesc: false,
      fields: [
        { key: "sn", label: "S/n", sortable: true },
        { key: "relief_name", label: "Relief Name", sortable: true },
      ],
      reliefName: "",
      reliefID: null,
      submitted: false,
    };
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <scale-loader v-if="apiBusy" />
    <div v-else>
      <div class="d-flex justify-content-between align-items-start mb-3 flex-lg-row flex-column">
        <b-button class="btn btn-success" @click="$refs['add-relief'].show()">
          <i class="mdi mdi-plus mr-2"></i>
          Add Relief Type
        </b-button>
      </div>

      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <div class="row mt-4">
                <div class="col-sm-12 col-md-6">
                  <div id="relief-table_length" class="dataTables_length">
                    <label class="d-inline-flex align-items-center">
                      Show&nbsp;
                      <b-form-select
                        v-model="perPage"
                        size="sm"
                        :options="pageOptions"
                      ></b-form-select
                      >&nbsp;entries
                    </label>
                  </div>
                </div>

                <div class="col-sm-12 col-md-6">
                  <div id="relief-table_filter" class="dataTables_filter text-md-right">
                    <label class="d-inline-flex align-items-center">
                      Search:
                      <b-form-input
                        v-model="filter"
                        type="search"
                        placeholder="Search..."
                        class="form-control form-control-sm ml-2"
                      ></b-form-input>
                    </label>
                  </div>
                </div>
              </div>

              <div class="table-responsive mb-0">
                <b-table
                  ref="relief-table"
                  bordered
                  selectable
                  hover
                  :items="reliefTypes"
                  :fields="fields"
                  responsive="sm"
                  :per-page="perPage"
                  :current-page="currentPage"
                  :sort-by.sync="sortBy"
                  :sort-desc.sync="sortDesc"
                  :filter="filter"
                  :filter-included-fields="filterOn"
                  @filtered="onFiltered"
                  show-empty
                  select-mode="single"
                  @row-selected="selectReliefType"
                ></b-table>
              </div>

              <div class="row">
                <div class="col">
                  <div class="dataTables_paginate paging_simple_numbers float-right">
                    <ul class="pagination pagination-rounded mb-0">
                      <b-pagination
                        v-model="currentPage"
                        :total-rows="totalRows"
                        :per-page="perPage"
                      ></b-pagination>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Relief Modal -->
    <b-modal ref="add-relief" title="Add Relief Type" hide-footer centered @hidden="resetForm">
      <form @submit.prevent="submitAdd">
        <div class="form-group">
          <label for="relief-name">Relief Name <span class="text-danger">*</span></label>
          <input
            id="relief-name"
            type="text"
            v-model="reliefName"
            class="form-control"
            :class="{ 'is-invalid': submitted && $v.reliefName.$error }"
          />
        </div>
        <b-button class="btn btn-success btn-block mt-4" type="submit">
          Submit
        </b-button>
      </form>
    </b-modal>

    <!-- Update Relief Modal -->
    <b-modal ref="update-relief" title="Update Relief Type" hide-footer centered @hidden="resetForm">
      <form @submit.prevent="submitUpdate">
        <div class="form-group">
          <label for="relief-name">Relief Name <span class="text-danger">*</span></label>
          <input
            id="relief-name"
            type="text"
            v-model="reliefName"
            class="form-control"
            :class="{ 'is-invalid': submitted && $v.reliefName.$error }"
          />
        </div>
        <b-button class="btn btn-success btn-block mt-4" type="submit">
          Update
        </b-button>
      </form>
    </b-modal>
  </Layout>
</template>
